// ─────────────────────────────────────────────────────────────
// ZIFEK — Réservation : types & calcul des créneaux disponibles.
// Partagé entre la page de réservation publique et la réservation
// rapide proposée par l'assistant IA.
// ─────────────────────────────────────────────────────────────

export interface BookingService {
  id: number;
  owner: number;
  service_name: string;
  duration: number;
  location: string | null;
  description: string | null;
  image?: string | null;
}

export interface BookingAvailability {
  id: number;
  day_of_week: string;
  start_time: string;
  end_time: string;
}

export interface BookingBreak {
  id: number;
  day_of_week: string;
  start_time: string;
  end_time: string;
}

export interface BookingHoliday {
  id: number;
  date_from: string;
  date_to: string;
  title: string;
}

export interface BookingAppointment {
  id: number;
  date: string;
  time: string;
}

export const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
export const DAY_LABELS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
export const SHORT_DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
export const MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

export function toDateStr(d: Date): string {
  return d.toISOString().split('T')[0];
}

export function parseTime(t: string): { h: number; m: number; total: number } {
  const [h, m] = t.slice(0, 5).split(':').map(Number);
  return { h, m, total: h * 60 + m };
}

export function addMinutes(timeStr: string, mins: number): string {
  const total = parseTime(timeStr).total + mins;
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

export function timeOverlap(aStart: string, aEnd: string, bStart: string, bEnd: string): boolean {
  return parseTime(aStart).total < parseTime(bEnd).total && parseTime(aEnd).total > parseTime(bStart).total;
}

export function dayKeyFor(dateStr: string): string {
  const d = new Date(dateStr);
  return DAY_KEYS[(d.getDay() + 6) % 7];
}

/** Créneaux réservables pour une date donnée. */
export function computeAvailableSlots(
  dateStr: string,
  service: BookingService,
  availabilities: BookingAvailability[],
  breaks: BookingBreak[],
  appointments: BookingAppointment[],
): string[] {
  if (!service) return [];
  const dayKey = dayKeyFor(dateStr);
  const avail = availabilities.find((a) => a.day_of_week === dayKey);
  if (!avail) return [];

  const dayBreaks = breaks.filter((b) => b.day_of_week === dayKey);
  const dayAppts = appointments.filter((a) => a.date === dateStr);
  const now = new Date();
  const isToday = dateStr === toDateStr(now);
  const currentMinutes = now.getHours() * 60 + now.getMinutes();

  const slots: string[] = [];
  const startTotal = parseTime(avail.start_time).total;
  const endTotal = parseTime(avail.end_time).total;

  for (let t = startTotal; t + service.duration <= endTotal; t += service.duration) {
    const slotStart = `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
    const slotEnd = addMinutes(slotStart, service.duration);
    if (isToday && t <= currentMinutes) continue;
    if (dayBreaks.some((b) => timeOverlap(slotStart, slotEnd, b.start_time, b.end_time))) continue;
    if (dayAppts.some((a) => timeOverlap(slotStart, slotEnd, a.time, addMinutes(a.time, service.duration)))) continue;
    slots.push(slotStart);
  }
  return slots;
}

/** Une date est-elle réservable (jour ouvré, hors congés, avec au moins un créneau) ? */
export function isDateBookable(
  dateStr: string,
  service: BookingService,
  availabilities: BookingAvailability[],
  holidays: BookingHoliday[],
  breaks: BookingBreak[],
  appointments: BookingAppointment[],
): boolean {
  if (!service) return false;
  if (dateStr < toDateStr(new Date())) return false;
  if (holidays.some((h) => dateStr >= h.date_from && dateStr <= h.date_to)) return false;
  return computeAvailableSlots(dateStr, service, availabilities, breaks, appointments).length > 0;
}

/** Formate une date en français long. */
export function formatDateFr(d: string): string {
  if (!d) return '';
  return new Date(d).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}